import {
  CheckCircleIcon,
  ClockIcon,
  EnvelopeSimpleIcon,
  PaperPlaneTiltIcon,
  WarningCircleIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCreateTelegramLink,
  useNotificationChannels,
  useUnlinkTelegram,
  type NotificationChannel,
} from '@entities/notification-channel';
import { ApiError } from '@shared/api/problem';
import { useAppShell } from '@shared/lib/appShell';
import { Button, ButtonLink, Card, Container, Skeleton, StateBlock } from '@shared/ui';

import styles from './ChannelsPage.module.css';

/**
 * N-03 Каналы уведомлений (FE-160, MVP_PRD §21.6).
 *
 * Экран держит три вещи:
 *
 * 1. **состояние каналов** — не подключён, ожидает подтверждения, подключён,
 *    ошибка, срок ссылки истёк. Пять состояний, а не «вкл/выкл»: «подключите
 *    Telegram» на экране, где ссылка уже выдана и протухла, ничего не
 *    объясняет;
 * 2. **подключение и отвязку Telegram** одной кнопкой;
 * 3. **объяснение, почему email не отключается**. Копии писем переговоров —
 *    часть услуги, а не рассылка: человек обязан видеть, что именно Karl
 *    написал автохаусу от его имени.
 *
 * Отсчёт до истечения ссылки идёт от серверного `expires_at`, а не от часов
 * браузера (§8 CLAUDE.md).
 */
export function ChannelsPage() {
  const { t } = useTranslation();
  const channels = useNotificationChannels();
  const createLink = useCreateTelegramLink();
  const unlink = useUnlinkTelegram();

  // Экран вне воронки заказа: трекер семи шагов на нём не рисуется, рельс Karl
  // не нужен — переписки здесь нет, есть настройка каналов.
  useAppShell({ stage: null, paid: false, karl: null });

  const [expired, setExpired] = useState(false);
  const link = createLink.data ?? null;

  useEffect(() => {
    if (link === null) return undefined;
    setExpired(false);
    const remaining = new Date(link.expires_at).getTime() - Date.now();
    if (remaining <= 0) {
      setExpired(true);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setExpired(true);
    }, remaining);
    return () => {
      window.clearTimeout(timer);
    };
  }, [link]);

  if (channels.isPending) {
    return (
      <Container>
        <div className={styles.page}>
          <Skeleton height="2rem" width="14rem" />
          <Skeleton height="8rem" radius="var(--radius-card)" />
          <Skeleton height="8rem" radius="var(--radius-card)" />
        </div>
      </Container>
    );
  }

  if (channels.isError) {
    return (
      <Container>
        <div className={styles.page}>
          <StateBlock
            tone="error"
            title={t('channels.error.title')}
            description={t('channels.error.description')}
            action={
              <Button variant="secondary" onClick={() => void channels.refetch()}>
                {t('common.retry')}
              </Button>
            }
          />
        </div>
      </Container>
    );
  }

  const email = channels.data.items.find((item) => item.channel === 'email') ?? null;
  const telegram = channels.data.items.find((item) => item.channel === 'telegram') ?? null;
  const telegramStatus = telegramState(telegram, expired);
  // Красная кнопка на экране одна (DESIGN_SYSTEM §5.1). Пока живая ссылка не
  // выдана, главное действие — «подключить»; когда выдана — «открыть бота»,
  // а повторный запрос ссылки отходит во второстепенные.
  const linkShown = link !== null && !expired && telegramStatus !== 'active';

  return (
    <Container>
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>{t('channels.title')}</h1>
          <p className={styles.lead}>{t('channels.lead')}</p>
        </header>

        <Card className={styles.card}>
          <div className={styles.channelHead}>
            <span className={styles.channelIcon} aria-hidden="true">
              <EnvelopeSimpleIcon size={20} weight="regular" />
            </span>
            <div>
              <h2 className={styles.channelTitle}>{t('channels.email.title')}</h2>
              <p className={styles.status} data-tone="active">
                <CheckCircleIcon aria-hidden size={16} weight="bold" />
                {t('channels.status.active')}
              </p>
            </div>
          </div>
          {/* Объяснение обязательно: кнопки отключения нет, и без причины это
              выглядит как недоделка, а не как правило (MVP_PRD §21.6). */}
          <p className={styles.note}>{t('channels.email.mandatory')}</p>
          {email?.linked_at ? null : <p className={styles.note}>{t('channels.email.pending')}</p>}
        </Card>

        <Card className={styles.card}>
          <div className={styles.channelHead}>
            <span className={styles.channelIcon} aria-hidden="true">
              <PaperPlaneTiltIcon size={20} weight="regular" />
            </span>
            <div>
              <h2 className={styles.channelTitle}>{t('channels.telegram.title')}</h2>
              <p className={styles.status} data-tone={telegramStatus}>
                {/* Иконки линейные, как остальные на экране (DESIGN_SYSTEM §5.10);
                    у каждого состояния, кроме «не подключён», — своя. */}
                {telegramStatus === 'failed' ? (
                  <WarningIcon aria-hidden size={16} weight="bold" />
                ) : null}
                {telegramStatus === 'active' ? (
                  <CheckCircleIcon aria-hidden size={16} weight="bold" />
                ) : null}
                {telegramStatus === 'pending' ? (
                  <ClockIcon aria-hidden size={16} weight="bold" />
                ) : null}
                {telegramStatus === 'expired' ? (
                  <WarningCircleIcon aria-hidden size={16} weight="bold" />
                ) : null}
                {t(`channels.status.${telegramStatus}`)}
                {telegram?.telegram_username ? ` · @${telegram.telegram_username}` : ''}
              </p>
            </div>
          </div>

          <p className={styles.note}>{t('channels.telegram.description')}</p>

          {channels.data.telegram_available ? (
            <div className={styles.actions}>
              {telegramStatus === 'active' ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    unlink.mutate();
                  }}
                  disabled={unlink.isPending}
                >
                  {t('channels.telegram.unlink')}
                </Button>
              ) : (
                <Button
                  variant={linkShown ? 'secondary' : 'primary'}
                  onClick={() => {
                    createLink.mutate();
                  }}
                  disabled={createLink.isPending}
                >
                  {t('channels.telegram.connect')}
                </Button>
              )}

              {link !== null && telegramStatus !== 'active' ? (
                <div className={styles.link}>
                  {expired ? (
                    <p className={styles.note}>{t('channels.telegram.linkExpired')}</p>
                  ) : (
                    <>
                      {/* Ссылка открывается в боте: она одноразовая и живёт
                          15 минут, поэтому показывается рядом с кнопкой, а не
                          прячется в модалку. */}
                      <ButtonLink to={link.url} target="_blank" rel="noreferrer">
                        {t('channels.telegram.openBot')}
                      </ButtonLink>
                      <p className={styles.note}>{t('channels.telegram.linkHint')}</p>
                    </>
                  )}
                </div>
              ) : null}

              {createLink.isError ? (
                <StateBlock compact tone="error" title={linkErrorTitle(createLink.error, t)} />
              ) : null}
              {unlink.isError ? (
                <StateBlock compact tone="error" title={t('channels.telegram.unlinkFailed')} />
              ) : null}
            </div>
          ) : (
            <StateBlock
              compact
              title={t('channels.telegram.unavailable')}
              description={t('channels.telegram.unavailableHint')}
            />
          )}

          {telegramStatus === 'failed' ? (
            <p className={styles.note}>{t('channels.telegram.failedHint')}</p>
          ) : null}
        </Card>

        <p className={styles.footnote}>{t('channels.footnote')}</p>
      </div>
    </Container>
  );
}

/** Пять состояний канала, включая «срок ссылки истёк» (FE-160). */
function telegramState(
  channel: NotificationChannel | null,
  expired: boolean,
): 'active' | 'pending' | 'failed' | 'expired' | 'absent' {
  if (channel === null) return 'absent';
  if (channel.status === 'active') return 'active';
  if (channel.status === 'failed') return 'failed';
  if (channel.status === 'pending') return expired ? 'expired' : 'pending';
  return 'absent';
}

function linkErrorTitle(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError && error.problem.code === 'TELEGRAM_ALREADY_LINKED') {
    return t('channels.telegram.alreadyLinked');
  }
  return t('channels.telegram.linkFailed');
}
